import React from 'react';
import { IconButton, styled, Toolbar, Typography } from '@mui/material';
import MoreVert from '@mui/icons-material/MoreVert';
import SettingsIcon from '@mui/icons-material/Settings';
import clsx from 'clsx';
import { Row } from 'ui/Flex';
import { MenuOn } from 'ui/MenuOn';
import { MuiTableMoreItem, MuiTableToolbarItems } from './MuiTable';
import { TableSearch } from './TableSearch';
import { TableSearchOptions, TableSearchOptionsProps } from './TableSearchOptions';

const PREFIX = 'MuiTableToolbar';

const classes = {
    highlight: `${PREFIX}-highlight`,
    spacer: `${PREFIX}-spacer`,
    title: `${PREFIX}-title`,
    controlsHolder: `${PREFIX}-controlsHolder`
};

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
    padding: theme.spacing(1, 1, 0, 3),
    flexDirection: 'column',
    alignItems: 'flex-start',
    [`& .${classes.highlight}`]: {
        color: theme.palette.secondary.main
    },
    [`& .${classes.spacer}`]: {
        flexGrow: 10
    },
    [`& .${classes.title}`]: {
        flex: '0 0 auto'
    },
    [`& .${classes.controlsHolder}`]: {
        flexDirection: 'row',
        width: '100%',
        display: 'flex'
    }
}));

interface MuiTableToolbarProps {
    numSelected: number;
    items?: MuiTableToolbarItems;
    selected: string[];
    selectable: boolean;
    TableSearchOptionsProps?: TableSearchOptionsProps;
}

export const MuiTableToolbar: React.FC<MuiTableToolbarProps> = ({
    numSelected,
    items,
    selected,
    selectable
}) => {
    const settingsButtonRenderOn = React.useCallback(
        (toggle: (event: React.MouseEvent<HTMLButtonElement>) => void) => (
            <IconButton onClick={toggle} size="large">
                <SettingsIcon />
            </IconButton>
        ),
        []
    );

    const moreButtonRenderOn = React.useCallback(
        (toggle: (event: React.MouseEvent<HTMLButtonElement>) => void) => (
            <IconButton disabled={numSelected < 1} onClick={toggle} size="large">
                <MoreVert />
            </IconButton>
        ),
        [numSelected]
    );

    const getMoreButtonItems = React.useCallback((): MuiTableMoreItem[] => {
        const moreItems = items?.more?.items;
        const enrichedItems = Array.isArray(moreItems)
            ? moreItems.map((item: MuiTableMoreItem) => ({
                  ...item,
                  onClick: () => item.onClick(selected)
              }))
            : [];
        return enrichedItems;
    }, [items?.more?.items, selected]);

    const renderSettingsButton = React.useCallback(
        (): React.ReactNode => (
            <MenuOn variant="withCheckbox" items={items?.settings?.items} renderOn={settingsButtonRenderOn} />
        ),
        [items?.settings?.items, settingsButtonRenderOn]
    );

    const renderMoreButton = React.useCallback(
        (): React.ReactNode => <MenuOn items={getMoreButtonItems()} renderOn={moreButtonRenderOn} />,
        [getMoreButtonItems, moreButtonRenderOn]
    );

    if (!selectable && !items) {
        return null;
    }
    return (
        <StyledToolbar
            disableGutters
            className={clsx({
                [classes.highlight]: selectable && numSelected > 0
            })}
        >
            {items && (
                <div className={classes.controlsHolder}>
                    {items.TableSearchOptionsProps && (
                        <Row valign="center">
                            <TableSearchOptions {...items.TableSearchOptionsProps} />
                        </Row>
                    )}
                    {items.search && <TableSearch {...items.search} />}
                    <div className={classes.spacer} />
                    <Row>
                        <Row valign="center">
                            {items.settings && renderSettingsButton()}
                            {items.renderCustomElement && items.renderCustomElement()}
                            {selectable && items.more && renderMoreButton()}
                        </Row>
                    </Row>
                </div>
            )}
            {selectable && (
                <div className={classes.title}>
                    {numSelected > 0 && (
                        <Typography color="inherit" variant="subtitle1">
                            {numSelected} selected
                        </Typography>
                    )}
                </div>
            )}
        </StyledToolbar>
    );
};
